import { createAccessControl } from 'better-auth/plugins';
import { first } from 'lodash';
import type { UserRole } from '@/db/schema';

export const ac = createAccessControl({
  auth: ['me', 'sign-out', 'change-password', 'force-change-password'],
  dashboard: ['browse'],
  // Página "Meu Perfil" — qualquer usuário autenticado acessa a própria (o `AppLayout`
  // deriva o recurso do 1º segmento da URL pra todo mundo em `_app`, então precisa existir
  // aqui senão `can()` nega geral, admin incluso).
  profile: ['browse'],
  users: ['browse', 'create', 'read', 'update', 'delete'],
  uploads: ['browse', 'create'],
  artists: ['browse', 'create', 'read', 'update', 'delete'],
  songs: ['browse', 'create', 'read', 'update', 'delete'],
  setlists: ['browse', 'create', 'read', 'update', 'delete'],
} as const);

/** Acesso completo — papel `admin`. Ao criar um recurso novo, adicione-o ao `ac` e aqui. */
const fullAccess = {
  auth: Object.values(ac.statements.auth),
  dashboard: Object.values(ac.statements.dashboard),
  profile: Object.values(ac.statements.profile),
  users: Object.values(ac.statements.users),
  uploads: Object.values(ac.statements.uploads),
  artists: Object.values(ac.statements['artists']),
  songs: Object.values(ac.statements['songs']),
  setlists: Object.values(ac.statements['setlists']),
};

/** Acesso limitado — papel `user`. */
const limitedAccess = {
  auth: Object.values(ac.statements.auth),
  dashboard: Object.values(ac.statements.dashboard),
  profile: Object.values(ac.statements.profile),
  users: [],
  uploads: ['browse', 'create'],
  artists: ['browse', 'read'],
  songs: ['browse', 'read'],
  // repertório é do ministério: todo usuário cria, edita e exclui qualquer um
  setlists: ['browse', 'read', 'create', 'update', 'delete'],
} as const;

/**
 * Papéis com acesso administrativo completo (usado nos poucos lugares que precisam de uma
 * checagem binária admin/não-admin, ex.: quem pode resetar senha de outro usuário).
 * Permissões por recurso de verdade são as de `roles` abaixo.
 */
export const ADMIN_TIER_ROLES: UserRole[] = ['admin'];

export function isAdminTierRole(role: UserRole | string | undefined | null): boolean {
  return !!role && (ADMIN_TIER_ROLES as string[]).includes(role);
}

export const roles = {
  admin: ac.newRole(fullAccess),
  user: ac.newRole(limitedAccess),
};

/**
 * Checa permissão de um papel num recurso do `ac`. Este módulo é isomórfico (usado no client
 * e no server), então NÃO pode depender do banco.
 */
export function can(role: UserRole, resource: string, action: string) {
  const roleDef = roles[role];

  if (!roleDef) return false;

  if (!(resource in ac.statements)) return false;

  const { success } = (roleDef.authorize as any)({
    [resource]: [action],
  });

  return success;
}

export const findResourceAndAction = (path: string, method: string) => {
  path = path.replace(/^\/api\/v1\//, '');
  const resource = first(path.split('/')) ?? '';

  let action = '';

  if (resource === 'auth') {
    action = path.split('/')[1]!;
  } else {
    if (method === 'GET') action = 'browse';
    else if (method === 'POST') action = 'create';
    else if (method === 'PUT') action = 'update';
    else if (method === 'PATCH') action = 'update';
    else if (method === 'DELETE') action = 'delete';
  }

  return {
    resource,
    action,
  };
};
